import React, { useEffect, useMemo, useState } from "react";
import { Alert, Badge, Button, Card, Col, Form, Modal, Row, Spinner, Table } from "react-bootstrap";
import {
  getSupplierEngagementSubmissions,
  respondSupplierEngagementSubmission,
} from "../api/api";

const safeArray = (value) => (Array.isArray(value) ? value : []);

function mediaUrl(path) {
  if (!path) return null;
  if (path.startsWith("http://") || path.startsWith("https://")) return path;
  return `http://localhost:8000${path}`;
}

function valueToText(value) {
  if (value === true) return "Oui";
  if (value === false) return "Non";
  if (value === null || value === undefined || value === "") return "-";
  if (Array.isArray(value)) return `${value.length} element(s)`;
  if (typeof value === "object") return JSON.stringify(value);
  return String(value);
}

function htmlEscape(value) {
  return String(value ?? "")
    .replaceAll("&", "&amp;")
    .replaceAll("<", "&lt;")
    .replaceAll(">", "&gt;")
    .replaceAll('"', "&quot;")
    .replaceAll("'", "&#039;");
}

function fullName(firstName, lastName, fallback = "-") {
  return `${firstName || ""} ${lastName || ""}`.trim() || fallback;
}

function personInfoText(person) {
  if (!person) return "";
  return [
    person.cin ? `CIN ${person.cin}` : null,
    person.matricule ? `Matricule ${person.matricule}` : null,
    person.telephone ? `Tel ${person.telephone}` : null,
    person.date_naissance ? `Ne(e) le ${person.date_naissance}` : null,
    person.age !== undefined && person.age !== null ? `${person.age} ans` : null,
    person.sexe ? `Sexe ${person.sexe}` : null,
    person.classe ? `Classe ${person.classe}` : null,
    person.etablissement || null,
    person.situation ? `Situation ${person.situation}` : null,
  ].filter(Boolean).join(" | ");
}

function resolveSubmissionValue(submission, key, value) {
  const normalizedKey = String(key || "").toLowerCase();
  if (normalizedKey.includes("enfant")) {
    const child = safeArray(submission?.adherent_enfants).find((item) => String(item.id) === String(value));
    if (child) return { label: fullName(child.prenom, child.nom), details: personInfoText(child) };
  }
  if (normalizedKey.includes("parent")) {
    const parent = safeArray(submission?.adherent_parents).find((item) => String(item.id) === String(value));
    if (parent) {
      return {
        label: `${parent.type_parent || "Parent"}: ${fullName(parent.prenom, parent.nom)}`,
        details: personInfoText(parent),
      };
    }
  }
  return { label: valueToText(value), details: "" };
}

function orderedDataEntries(data) {
  return Object.entries(data || {})
    .filter(([key, value]) => {
      const isRemark = ["remarque", "remarques", "remark", "remarks"].includes(String(key).toLowerCase());
      if (!isRemark) return true;
      return !(value === null || value === undefined || value === "" || value === "-");
    })
    .sort(([a], [b]) => {
      const ar = ["remarque", "remarques", "remark", "remarks"].includes(String(a).toLowerCase());
      const br = ["remarque", "remarques", "remark", "remarks"].includes(String(b).toLowerCase());
      if (ar === br) return 0;
      return ar ? 1 : -1;
    });
}

function statusBadge(status) {
  if (status === "pending") return <Badge bg="info">A repondre</Badge>;
  if (status === "approved") return <Badge bg="success">Accepte</Badge>;
  if (status === "rejected") return <Badge bg="danger">Refuse</Badge>;
  return <Badge bg="secondary">Non envoye</Badge>;
}

export default function DashboardFournisseur() {
  const [loading, setLoading] = useState(false);
  const [submissions, setSubmissions] = useState([]);
  const [selected, setSelected] = useState(null);
  const [accepted, setAccepted] = useState("");
  const [reason, setReason] = useState("");
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState("");

  const pendingCount = useMemo(
    () => submissions.filter((item) => item.supplier_status === "pending").length,
    [submissions]
  );

  const fetchSubmissions = async () => {
    setLoading(true);
    try {
      const res = await getSupplierEngagementSubmissions();
      setSubmissions(safeArray(res.data));
    } catch (err) {
      setError("Impossible de charger les demandes fournisseur.");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchSubmissions();
  }, []);

  const openDetails = (submission) => {
    setSelected(submission);
    setAccepted("");
    setReason("");
    setError("");
  };

  const submitResponse = async () => {
    if (!selected?.id) return;
    const yesNo = accepted === "yes";
    if (!accepted) {
      setError("Choisissez Oui ou Non.");
      return;
    }
    if (!yesNo && !reason.trim()) {
      setError("La cause est obligatoire si la reponse est Non.");
      return;
    }

    setSaving(true);
    setError("");
    try {
      await respondSupplierEngagementSubmission(selected.id, yesNo, reason.trim());
      setSelected(null);
      await fetchSubmissions();
    } catch (err) {
      setError(
        err?.response?.data?.reason ||
          err?.response?.data?.detail ||
          "Impossible d'envoyer la reponse."
      );
    } finally {
      setSaving(false);
    }
  };

  const exportSubmissionDetailPdf = (submission) => {
    if (!submission) return;
    const dataRows = orderedDataEntries(submission.data || {}).map(([key, value]) => {
      const resolved = resolveSubmissionValue(submission, key, value);
      return `
        <tr>
          <th>${htmlEscape(key.replaceAll("_", " "))}</th>
          <td>
            ${htmlEscape(resolved.label)}
            ${resolved.details ? `<br/><small>${htmlEscape(resolved.details)}</small>` : ""}
          </td>
        </tr>
      `;
    }).join("");
    const fileRows = safeArray(submission.files).map((file) => `
      <tr>
        <th>${htmlEscape(file.field_key)}</th>
        <td>${htmlEscape(mediaUrl(file.file) || "-")}</td>
      </tr>
    `).join("");

    const win = window.open("", "_blank");
    if (!win) return;
    win.document.write(`
      <html>
        <head>
          <title>Demande engagement #${htmlEscape(submission.id)}</title>
          <style>
            body { font-family: Arial, sans-serif; padding: 24px; color: #202124; }
            .header { display: flex; align-items: center; gap: 14px; border-bottom: 2px solid #1f7a2f; padding-bottom: 12px; margin-bottom: 20px; }
            .header img { width: 58px; height: 58px; object-fit: contain; }
            .brand { font-weight: 800; color: #1f7a2f; letter-spacing: .08em; text-transform: uppercase; font-size: 12px; }
            h1 { font-size: 22px; margin: 0 0 6px; }
            table { width: 100%; border-collapse: collapse; margin-top: 14px; font-size: 13px; }
            th, td { border: 1px solid #d7dce1; padding: 9px; text-align: left; vertical-align: top; }
            th { width: 34%; background: #f6f8fa; }
            .section { margin-top: 22px; font-weight: 700; }
          </style>
        </head>
        <body>
          <div class="header">
            <img src="/logoamicale.png" alt="Amicale" />
            <div>
              <div class="brand">Amicale Autoroutes</div>
              <strong>Fiche detaillee de demande</strong>
            </div>
          </div>
          <h1>Demande engagement #${htmlEscape(submission.id)}</h1>
          <table>
            <tr><th>Engagement</th><td>${htmlEscape(submission.engagement_title || "-")}</td></tr>
            <tr><th>Adherent</th><td>${htmlEscape(fullName(submission.adherent_first_name, submission.adherent_last_name))}</td></tr>
            <tr><th>Matricule</th><td>${htmlEscape(submission.adherent_matricule || "-")}</td></tr>
            <tr><th>CIN</th><td>${htmlEscape(submission.adherent_cin || "-")}</td></tr>
            <tr><th>Telephone</th><td>${htmlEscape(submission.adherent_telephone || "-")}</td></tr>
            <tr><th>Date demande</th><td>${htmlEscape(submission.created_at ? new Date(submission.created_at).toLocaleString() : "-")}</td></tr>
          </table>
          <div class="section">Reponses</div>
          <table>${dataRows || "<tr><td>Aucune reponse</td></tr>"}</table>
          ${fileRows ? `<div class="section">Pieces jointes</div><table>${fileRows}</table>` : ""}
        </body>
      </html>
    `);
    win.document.close();
    win.focus();
    win.print();
  };

  const dataEntries = orderedDataEntries(selected?.data || {});

  return (
    <div>
      <div className="d-flex justify-content-between align-items-start gap-3 mb-4">
        <div>
          <span className="profile-eyebrow">Demandes</span>
          <h2 className="mb-2" style={{ fontWeight: 900 }}>
            Dashboard fournisseur
          </h2>
          <p className="text-muted mb-0">
            Les demandes approuvees par l'admin arrivent ici pour votre reponse.
          </p>
        </div>
        <Button variant="outline-secondary" onClick={fetchSubmissions}>
          Rafraichir
        </Button>
      </div>

      <Row className="g-3 mb-4">
        <Col md={4}>
          <Card>
            <Card.Body>
              <div className="text-muted">A repondre</div>
              <h3 className="mb-0">{pendingCount}</h3>
            </Card.Body>
          </Card>
        </Col>
        <Col md={4}>
          <Card>
            <Card.Body>
              <div className="text-muted">Total recu</div>
              <h3 className="mb-0">{submissions.length}</h3>
            </Card.Body>
          </Card>
        </Col>
      </Row>

      {error && !selected && <Alert variant="danger">{error}</Alert>}

      {loading ? (
        <div className="text-center py-5">
          <Spinner animation="border" />
        </div>
      ) : submissions.length === 0 ? (
        <Alert variant="light">Aucune demande transmise pour le moment.</Alert>
      ) : (
        <Card>
          <Card.Body>
            <Table responsive hover className="mb-0">
              <thead>
                <tr>
                  <th>ID</th>
                  <th>Engagement</th>
                  <th>Adherent</th>
                  <th>Statut</th>
                  <th>Date reception</th>
                  <th>Actions</th>
                </tr>
              </thead>
              <tbody>
                {submissions.map((item) => (
                  <tr key={item.id}>
                    <td>{item.id}</td>
                    <td>{item.engagement_title || "-"}</td>
                    <td>
                      <strong>
                        {item.adherent_first_name || "-"} {item.adherent_last_name || ""}
                      </strong>
                      <div className="text-muted" style={{ fontSize: 12 }}>
                        {item.adherent_matricule || "-"}
                        {item.adherent_cin ? ` | CIN ${item.adherent_cin}` : ""}
                      </div>
                    </td>
                    <td>
                      {statusBadge(item.supplier_status)}
                      {item.supplier_refusal_reason && (
                        <div className="text-muted mt-1" style={{ fontSize: 12 }}>
                          {item.supplier_refusal_reason}
                        </div>
                      )}
                    </td>
                    <td>
                      {item.forwarded_to_supplier_at
                        ? new Date(item.forwarded_to_supplier_at).toLocaleString()
                        : "-"}
                    </td>
                    <td>
                      <Button size="sm" variant="outline-primary" onClick={() => openDetails(item)}>
                        Voir / repondre
                      </Button>
                      <Button size="sm" variant="outline-info" className="ms-2" onClick={() => exportSubmissionDetailPdf(item)}>
                        PDF
                      </Button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </Table>
          </Card.Body>
        </Card>
      )}

      <Modal show={!!selected} onHide={() => setSelected(null)} size="lg" centered>
        <Modal.Header closeButton>
          <Modal.Title>Detail de la demande</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          {selected && (
            <>
              <Row className="g-3 mb-3">
                <Col md={6}>
                  <Card>
                    <Card.Body>
                      <div className="text-muted" style={{ fontSize: 12 }}>Adherent</div>
                      <strong>
                        {selected.adherent_first_name || "-"} {selected.adherent_last_name || ""}
                      </strong>
                      <div className="text-muted">{selected.adherent_matricule || "-"}</div>
                      <div className="text-muted">CIN {selected.adherent_cin || "-"}</div>
                      <div className="text-muted">Tel {selected.adherent_telephone || "-"}</div>
                    </Card.Body>
                  </Card>
                </Col>
                <Col md={6}>
                  <Card>
                    <Card.Body>
                      <div className="text-muted" style={{ fontSize: 12 }}>Engagement</div>
                      <strong>{selected.engagement_title || "-"}</strong>
                      <div className="mt-2">{statusBadge(selected.supplier_status)}</div>
                    </Card.Body>
                  </Card>
                </Col>
              </Row>

              <Card className="mb-3">
                <Card.Body>
                  <h6>Reponses adherent</h6>
                  {dataEntries.length === 0 ? (
                    <Alert variant="warning" className="mb-0">Aucune reponse.</Alert>
                  ) : (
                    dataEntries.map(([key, value]) => {
                      const resolved = resolveSubmissionValue(selected, key, value);
                      return (
                        <div key={key} className="d-flex justify-content-between border-bottom py-2">
                          <span className="text-muted">{key.replaceAll("_", " ")}</span>
                          <strong style={{ textAlign: "right" }}>
                            {resolved.label}
                            {resolved.details && (
                              <small className="d-block text-muted" style={{ fontWeight: 400 }}>
                                {resolved.details}
                              </small>
                            )}
                          </strong>
                        </div>
                      );
                    })
                  )}
                </Card.Body>
              </Card>

              {safeArray(selected.files).length > 0 && (
                <Card className="mb-3">
                  <Card.Body>
                    <h6>Pieces jointes</h6>
                    {selected.files.map((file) => (
                      <div key={file.id} className="d-flex justify-content-between border-bottom py-2">
                        <span>{file.field_key}</span>
                        <Button size="sm" href={mediaUrl(file.file)} target="_blank" rel="noreferrer">
                          Ouvrir
                        </Button>
                      </div>
                    ))}
                  </Card.Body>
                </Card>
              )}

              {selected.supplier_status === "pending" ? (
                <Card>
                  <Card.Body>
                    <h6>Votre reponse</h6>
                    <div className="d-flex gap-4 mb-3">
                      <Form.Check
                        type="radio"
                        name="supplier-response"
                        label="Oui"
                        checked={accepted === "yes"}
                        onChange={() => setAccepted("yes")}
                      />
                      <Form.Check
                        type="radio"
                        name="supplier-response"
                        label="Non"
                        checked={accepted === "no"}
                        onChange={() => setAccepted("no")}
                      />
                    </div>
                    {accepted === "no" && (
                      <Form.Group>
                        <Form.Label>Cause du refus</Form.Label>
                        <Form.Control
                          as="textarea"
                          rows={3}
                          value={reason}
                          onChange={(event) => setReason(event.target.value)}
                        />
                      </Form.Group>
                    )}
                  </Card.Body>
                </Card>
              ) : (
                <Alert variant="light" className="mb-0">
                  Reponse deja envoyee.
                </Alert>
              )}

              {error && <Alert variant="danger" className="mt-3">{error}</Alert>}
            </>
          )}
        </Modal.Body>
        <Modal.Footer>
          <Button variant="secondary" onClick={() => setSelected(null)}>
            Fermer
          </Button>
          <Button variant="outline-info" onClick={() => exportSubmissionDetailPdf(selected)}>
            Imprimer PDF
          </Button>
          {selected?.supplier_status === "pending" && (
            <Button variant="primary" onClick={submitResponse} disabled={saving}>
              {saving ? "Envoi..." : "Envoyer la reponse"}
            </Button>
          )}
        </Modal.Footer>
      </Modal>
    </div>
  );
}
